import * as React from "react";
import { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Select,
  MenuItem,
  InputBase,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SearchIcon from "@mui/icons-material/Search";
import { API_ENDPOINT_URL } from "../../util/api";
import { useContextAuthentication } from "../../util/useContextAuth";
import { resolveApiMessage } from "../../locales/apiMessageMap";

// ─── Constants ────────────────────────────────────────────────────────────────

// Ordered to follow the backend detection pipeline (broker/utils.py run_detect_pipeline):
//   Step 1 (parallel): whitecheck, urlcheck, urldet, whois, dns
//   Step 2 (if URL live): sitecrawle
//   Step 3 (if crawled):  htmldet, visual_logodet
//   Step 4 (if logodet):  visual_branddet
//   Step 5:               meta_analysis
const DETECTOR_ASPECTS = [
  // Step 1 — initial parallel checks
  { key: "urldet", aspect: "Address spoofing / tampering inspection" },
  { key: "whitecheck", aspect: "Reputation verification" },
  { key: "whois", aspect: "Domain registration analysis" },
  { key: "dns", aspect: "DNS infrastructure inspection" },
  { key: "urlcheck", aspect: "URL response inspection" },

  // Step 2 — website crawling (requires live URL)
  { key: "sitecrawle", aspect: "Website crawling inspection" },

  // Step 3 — content & logo analysis (requires crawled page)
  { key: "htmldet", aspect: "Page content / form structure inspection" },
  { key: "visual_logodet", aspect: "Visual logo imitation inspection" },

  // Step 4 — brand imitation (requires logo detection)
  {
    key: "visual_branddet",
    fallbackKey: "visualdet",
    aspect: "Visual brand imitation inspection",
  },

  // Step 5 — site characteristics
  { key: "meta_analysis", aspect: "Site characteristics analysis" },
];

// ─── Derive display data from API response ────────────────────────────────────

function deriveFromApiData(apiData) {
  const ct = apiData.classify_threat ?? {};
  const isPhish = apiData.is_phish ?? false;
  const ct_isPhish = ct.is_phishing ?? false;
  const meta = apiData.meta_analysis ?? {};

  // A — strength of evidence (all detectors judged phishing)
  const phishCount = DETECTOR_ASPECTS.filter((def) => {
    const det =
      apiData[def.key] ?? (def.fallbackKey ? apiData[def.fallbackKey] : null);
    return det?.is_phish === true;
  }).length;

  const riskLevel = isPhish ? (ct.risk_level ?? "none") : "none";

  // C/E — detector rows
  const detectorRows = DETECTOR_ASPECTS.map((def) => {
    const det =
      apiData[def.key] ?? (def.fallbackKey ? apiData[def.fallbackKey] : null);
    if (!det)
      return {
        aspect: def.aspect,
        detKey: def.key,
        rawDet: null,
        executionStatus: "Not run",
        skipReason: "Detector not available",
        is_phish: null,
        verdict: "N/A",
        riskLevel: null,
      };

    // visual_branddet wraps inner data under .visualdet
    const rawDet = def.key === "visual_branddet" ? (det.visualdet ?? det) : det;

    let executionStatus = "Completed";
    let skipReason = "—";
    if (det.status === "error") {
      executionStatus = "Error";
      skipReason = (det.message ?? "Unknown error").slice(0, 80);
    } else if (def.key === "dns" && det.errors?.length > 0) {
      executionStatus = "Error";
      skipReason = String(det.errors[0] ?? "Unknown error").slice(0, 80);
    } else if (def.key === "urlcheck" && det.is_live === false) {
      skipReason = det.msg ?? "URL not reachable";
    }

    // Some detectors (dns, urlcheck, whois, meta_analysis) have no is_phish → null.
    const is_phish = det.is_phish ?? null;

    return {
      aspect: def.aspect,
      detKey: def.key,
      rawDet,
      executionStatus,
      skipReason,
      is_phish,
      verdict:
        is_phish === true ? "PHISHING" : is_phish === false ? "SAFE" : "N/A",
      // Only a phishing verdict carries a risk level; SAFE / N/A show none ("—").
      // (The whitecheck row overrides this to Low/green in Section C.)
      riskLevel: is_phish === true ? "Critical" : null,
    };
  });

  // Risk level (backend) → display key used by RISK_COLOR / riskLevelLabels
  const RISK_DISPLAY = {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
    none: "None",
  };
  const overallRiskDisplay = ct_isPhish
    ? (RISK_DISPLAY[ct.risk_level] ?? "High")
    : "None";

  const mapThreat = (t, i) => ({
    no: i + 1,
    type: t.name,
    code: t.code,
    basis: t.basis,
    basisKey: t.basis_key,
    basisParams: t.basis_params ?? {},
    confidence: t.confidence,
    riskLevel: RISK_DISPLAY[t.risk_level] ?? RISK_DISPLAY["low"],
  });

  // B — high/critical threats only
  const threatTypesCritical = (ct.threats ?? [])
    .filter((t) => t.risk_level === "high" || t.risk_level === "critical")
    .map(mapThreat);

  // G — all threats with risk_level from backend
  const threatTypesAll = (ct.threats ?? []).map(mapThreat);

  // F — all findings from classify_threat
  const findingsAll = ct.findings ?? [];

  // D — top-5 site-characteristic elements from meta_analysis (BE pre-ranked).
  //   is_phish === true  → top_phish  (ratio = positive_ratio)
  //   is_phish === false → top_legit  (ratio = negative_ratio)
  const siteCharIsPhish = isPhish;
  const siteCharTop = (isPhish ? meta.top_phish : meta.top_legit) ?? [];

  // C (Layer2-C) — per-detector results with conditional visibility:
  //   urldet:     always shown
  //   htmldet:    shown when HTML was obtained; phishing if is_phish, else safe
  //   visualdet:  shown when the screenshot (scsh) was obtained; phishing if
  //               is_phish, else safe
  //   whitecheck: shown only when the domain is in the whitelist
  //               (BE returns whitecheck.is_phish === false) → verdict safe
  const hasHtml = Boolean(apiData.html);
  const hasScsh = Boolean(apiData.scsh);
  const inWhitelist = (apiData.whitecheck ?? {}).is_phish === true;
  const rowByKey = (key) => detectorRows.find((r) => r.detKey === key);

  // Content detectors are always phishing-or-safe (never N/A):
  // is_phish === true → phishing, otherwise safe.
  const withPhishVerdict = (row) =>
    row && {
      ...row,
      verdict: row.is_phish === true ? "PHISHING" : "SAFE",
      riskLevel: row.is_phish === true ? "Critical" : null,
    };

  const aiInspectionSummary = [];

  const urldetRow = rowByKey("urldet");
  if (urldetRow) aiInspectionSummary.push(urldetRow); // urldet — always shown

  if (hasHtml) {
    const htmldetRow = withPhishVerdict(rowByKey("htmldet"));
    if (htmldetRow) aiInspectionSummary.push(htmldetRow);
  }

  if (hasScsh) {
    const visualRow = withPhishVerdict(rowByKey("visual_branddet")); // visualdet
    if (visualRow) aiInspectionSummary.push(visualRow);
  }

  if (inWhitelist) {
    const wcRow = rowByKey("whitecheck");
    if (wcRow)
      aiInspectionSummary.push({
        ...wcRow,
        verdict: "SAFE", // 判定 = safe(安全)
        riskLevel: "Low", // リスクレベル = low(低)
      });
  }

  return {
    phishCount,
    overallRiskLevelKey: riskLevel,
    overallVerdict: {
      verdict: isPhish ? "PHISHING" : "SAFE",
      riskLevel: overallRiskDisplay,
    },
    threatTypesCritical,
    aiInspectionSummary,
    // 検知結果 (Detection result) — mirrors A. Overall Verdict above.
    detectionResult: {
      verdict: isPhish ? "PHISHING" : "SAFE",
      riskLevel: isPhish ? "Critical" : "none",
    },
    aiInspectionDetail: detectorRows, // E
    siteCharTop, // D — top-5 phishing/legit elements
    siteCharIsPhish, // D — true = phishing elements, false = legit elements
    findingsAll, // F — all findings
    threatTypesAll, // G
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const RISK_COLOR = {
  Critical: "#dc3545",
  High: "#dc3545",
  Medium: "#fd7e14",
  Low: "#ffc107",
};

// Green used for safe/whitelisted indicators (matches the SAFE verdict color).
const SAFE_GREEN = "#28a745";

// Thumbnail that opens a full-size popup on click
function ZoomableImage({ src, alt }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img
        src={src}
        alt={alt}
        onClick={() => setOpen(true)}
        style={{
          maxWidth: 320,
          maxHeight: 200,
          marginTop: 6,
          border: "1px solid #ddd",
          cursor: "zoom-in",
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <Box
          onClick={() => setOpen(false)}
          sx={{ p: 1, background: "#000", cursor: "zoom-out" }}
        >
          <img
            src={src}
            alt={alt}
            style={{ display: "block", maxWidth: "70vw", maxHeight: "65vh" }}
          />
        </Box>
      </Dialog>
    </>
  );
}

function DetectorNotes({ detKey, rawDet }) {
  const { t } = useTranslation();
  if (!rawDet) return "—";
  const score = rawDet.score != null ? Number(rawDet.score).toFixed(3) : null;

  if (detKey === "urldet") {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {score != null && (
          <div>
            {t("ui.notes.score")}: <strong>{score}</strong>
          </div>
        )}
        {rawDet.domain && (
          <div>
            {t("ui.notes.domain")}: {rawDet.domain}
          </div>
        )}
      </Box>
    );
  }

  if (detKey === "htmldet") {
    const kw = rawDet.keyword ?? {};
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {score != null && (
          <div>
            {t("ui.notes.score")}: <strong>{score}</strong>
          </div>
        )}
        <div>
          {t("ui.notes.passwordForm")}:{" "}
          {rawDet.password_form ? t("ui.notes.found") : t("ui.notes.notFound")}
        </div>
        {(kw.login != null ||
          kw.password != null ||
          kw.authentication != null) && (
          <>
            <div>{t("ui.notes.keywordLogin")}: {kw.login ?? 0}</div>
            <div>{t("ui.notes.keywordPassword")}: {kw.password ?? 0}</div>
            <div>{t("ui.notes.keywordAuth")}: {kw.authentication ?? 0}</div>
          </>
        )}
      </Box>
    );
  }

  if (detKey === "visual_branddet") {
    const imgUrl = rawDet.path ?? rawDet.img_object ?? null;
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {score != null && (
          <div>
            {t("ui.notes.score")}: <strong>{score}</strong>
          </div>
        )}
        {rawDet.brand && (
          <div>
            {t("ui.notes.brand")}: {rawDet.brand}
          </div>
        )}
        {imgUrl && <ZoomableImage src={imgUrl} alt="screenshot" />}
      </Box>
    );
  }

  if (detKey === "visual_logodet") {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {score != null && (
          <div>
            {t("ui.notes.score")}: <strong>{score}</strong>
          </div>
        )}
        <div>{t("ui.notes.objects", { count: rawDet.result?.length ?? 0 })}</div>
      </Box>
    );
  }

  if (detKey === "whois") {
    const EXCLUDE = ["is_phish", "error"];
    // msg/message values are already self-describing — show them without a label.
    const MESSAGE_KEYS = ["msg", "message"];
    const fmtValue = (v) => {
      if (v === null || v === undefined) return "—";
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    };
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {Object.entries(rawDet)
          .filter(([k]) => !EXCLUDE.includes(k))
          .map(([k, v]) =>
            MESSAGE_KEYS.includes(k) ? (
              <div key={k} style={{ wordBreak: "break-all" }}>
                {t("ui.notes.msg", "Msg")}: {resolveApiMessage(String(v), "whois", t)}
              </div>
            ) : (
              <div key={k} style={{ wordBreak: "break-all" }}>
                {k === "execution_time"
                  ? t("ui.notes.execution_time", k)
                  : t(`ui.whoisFields.${k}`, k)}
                : {fmtValue(v)}
              </div>
            ),
          )}
      </Box>
    );
  }

  if (detKey === "whitecheck") {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {score != null && (
          <div>
            {t("ui.notes.score")}: <strong>{score}</strong>
          </div>
        )}
        {rawDet.is_crawled != null && (
          <div>
            {t("ui.notes.crawled")}: {String(rawDet.is_crawled)}
          </div>
        )}
      </Box>
    );
  }

  if (detKey === "urlcheck") {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        <div>
          {t("ui.notes.live")}: {rawDet.is_live ? t("ui.notes.yes") : t("ui.notes.no")}
        </div>

        {rawDet.n_redirect != null && (
          <div>
            {t("ui.notes.redirects")}: {rawDet.n_redirect}
          </div>
        )}
        {rawDet.domain && (
          <div>
            {t("ui.notes.domain")}: {rawDet.domain}
          </div>
        )}
      </Box>
    );
  }

  if (detKey === "dns") {
    const asArray = (arr) =>
      `[${(arr ?? []).map((v) => `'${v}'`).join(", ")}]`;
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        <div style={{ wordBreak: "break-all" }}>
          {t("ui.notes.forwardIps")}: {asArray(rawDet.forward_ips)}
        </div>
        <div style={{ wordBreak: "break-all" }}>
          {t("ui.notes.reversePtrs")}: {asArray(rawDet.reverse_ptrs)}
        </div>
        {rawDet.execution_time != null && (
          <div>
            {t("ui.notes.execution_time")}: {rawDet.execution_time}
          </div>
        )}
      </Box>
    );
  }

  if (detKey === "sitecrawle") {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {rawDet.msg ? (
          <div style={{ color: "#d32f2f" }}>
            {t("ui.notes.msg")}: {resolveApiMessage(rawDet.msg, "sitecrawle", t)}
          </div>
        ) : (
          <>
            <div style={{ wordBreak: "break-all" }}>
              {t("ui.notes.html")}: {rawDet.html}
            </div>
            <div style={{ wordBreak: "break-all" }}>
              {t("ui.notes.scsh")}: {rawDet.scsh}
            </div>
          </>
        )}
      </Box>
    );
  }

  if (detKey === "meta_analysis") {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.8 }}>
        {rawDet.corpus_size != null && (
          <div>
            {t("ui.notes.corpusSize")}: {rawDet.corpus_size?.toLocaleString()}
          </div>
        )}
        {rawDet.n_candidates != null && (
          <div>
            {t("ui.notes.candidates")}: {rawDet.n_candidates}
          </div>
        )}
      </Box>
    );
  }

  return score != null ? `${t("ui.notes.score")}: ${score}` : "—";
}

function VerdictCell({ verdict }) {
  const { t } = useTranslation();
  if (verdict === "PHISHING") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", fontWeight: 700 }}>
        <RiskDot level="Critical" />
        <span style={{ whiteSpace: "pre-line" }}>
          {t("ui.verdictValues.PHISHING", "PHISHING")}
        </span>
      </Box>
    );
  }
  if (verdict === "SAFE") {
    return (
      <Box sx={{ color: "#28a745", fontWeight: 600 }}>
        {t("ui.verdictValues.SAFE", "SAFE")}
      </Box>
    );
  }
  return (
    <span style={{ color: "#aaa" }}>{t("ui.verdictValues.NA", "N/A")}</span>
  );
}

function RiskDot({ level, color }) {
  return (
    <FiberManualRecordIcon
      sx={{
        fontSize: 14,
        color: color ?? RISK_COLOR[level] ?? "#999",
        mr: 0.5,
        verticalAlign: "middle",
      }}
    />
  );
}

function RiskCell({ level, color }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
      <RiskDot level={level} color={color} />
      <span>{t(`ui.riskLevelLabels.${level}`, level)}</span>
    </Box>
  );
}

const TH = ({ children, width }) => (
  <th
    style={{
      padding: "8px 12px",
      textAlign: "center",
      fontWeight: 600,
      fontSize: 15,
      background: "#f5f5f5",
      border: "1px solid #ccc",
      width,
    }}
  >
    {children}
  </th>
);

const TD = ({ children, style, colSpan }) => (
  <td
    colSpan={colSpan}
    style={{
      padding: "8px 12px",
      fontSize: 15,
      border: "1px solid #ccc",
      verticalAlign: "top",
      ...style,
    }}
  >
    {children}
  </td>
);

function SectionHeader({ letter, title }) {
  return (
    <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 3, mb: 1 }}>
      {letter}. {title}
    </Typography>
  );
}

function SubNote({ children }) {
  return (
    <Typography
      sx={{ fontSize: 14, color: "#777", fontStyle: "italic", mb: 1.5 }}
    >
      {children}
    </Typography>
  );
}

function LayerHeader({ num, title }) {
  return (
    <Box sx={{ mt: 4, mb: 1 }}>
      <Divider />
      <Typography sx={{ fontWeight: 700, fontSize: 20, mt: 2 }}>
        Layer {num} — {title}
      </Typography>
    </Box>
  );
}

// ─── TEST MOCK DATA (dev only) ─────────────────────────────────────────────────
// Only dns + whois. To preview these two sections with mock data, uncomment the
// `withMock(...)` calls at the two usage sites below (search handler + initial
// state). Leave them commented for normal use with real BE data.
const MOCK_DATA = {
  dns: {
    forward_success: true,
    forward_ips: ["142.251.211.14"],
    reverse_success: true,
    reverse_ptrs: ["pnorda-am-in-f14.1e100.net."],
    errors: [],
    execution_time: 0.34,
  },
  whois: {
    domain_name: "YOUTUBE.COM",
    registry_domain_id: "874763539_DOMAIN_COM-VRSN",
    registrar_whois_server: "whois.markmonitor.com",
    registrar_url: "http://www.markmonitor.com",
    updated_date: "2024-01-12",
    creation_date: "2005-02-15",
    registry_expiry_date: "2026-02-15",
    registrar: "MarkMonitor Inc.",
    registrar_iana_id: "292",
    msg: "Domain older than 9 months",
    is_phish: false,
    execution_time: 0.12,
  },
};

// Merge mock dns/whois into a real API response (dev only)
const withMock = (data) => (data ? { ...data, ...MOCK_DATA } : data);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AnalysisReport() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { state, pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const rid = searchParams.get("rid");
  const contextAuth = useContextAuthentication();
  const token = contextAuth?.value?.key;

  const initialData =
    state?.data ??
    (rid
      ? JSON.parse(sessionStorage.getItem(`report_${rid}`) ?? "null")
      : null);

  const [apiData, setApiData] = useState(initialData);
  // Dev: preview dns/whois with mock — use this line instead:
  // const [apiData, setApiData] = useState(withMock(initialData));
  // Pre-fill the search box with a URL handed over from the URL Lookup screen.
  const [searchText, setSearchText] = useState(state?.url ?? "");
  const [loading, setLoading] = useState(false);
  const [validateSearch, setValidateSearch] = useState(false);
  const [regisDialog, setRegisDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [alertMessage, setAlertMessage] = useState("");

  const fireAlert = (severity, message) => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  const handleSearch = async () => {
    if (searchText.trim() === "") {
      setValidateSearch(true);
      return;
    }
    setValidateSearch(false);

    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    if (!urlRegex.test(searchText)) {
      fireAlert("error", t("ui.search.invalidUrl"));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_ENDPOINT_URL}/data/lookup/`, {
        params: { search: searchText },
          headers: { Authorization: `Bearer ${token}` },
        timeout: 600 * 1000,
      });

      const data = response.data.data;
      if (data === null) {
        setApiData(null);
        setRegisDialog(true);
      } else {
        const finalData = data;
        // Dev: preview dns/whois with mock — use this line instead:
        // const finalData = withMock(data);
        setApiData(finalData);
        // Wipe any persisted report and drop the handed-over navigation state
        // (state.url / ?rid). On refresh (F5) the screen starts blank and the
        // user must re-enter a URL and search again — no stale data is loaded.
        sessionStorage.clear();
        navigate(pathname, { replace: true });
      }
    } catch (error) {
      fireAlert("error", t("ui.search.failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = (event) => {
    if (event.key === "Enter") handleSearch();
  };

  // Consume the params handed over from URL Lookup, then wipe them so a refresh
  // (F5) starts blank. Two cases:
  //   • result data was passed → it's already shown; just clear the carry-over.
  //   • only a URL was passed  → auto-run the search (handleSearch clears it on
  //                              success).
  // Runs once on mount.
  React.useEffect(() => {
    if (initialData) {
      sessionStorage.clear();
      navigate(pathname, { replace: true });
    } else if (state?.url) {
      handleSearch();
    }
    // Intentionally runs once on mount (empty deps).
  }, []);

  let d = null;
  let meta = null;
  let strengthOfEvidence = "";
  let recommendedAction = "—";
  let recommendedResponse = "—";

  if (apiData) {
    d = deriveFromApiData(apiData);
    strengthOfEvidence =
      d.phishCount === 0
        ? t("ui.noDetectorsTriggerred")
        : t("ui.detectedByCount", { count: d.phishCount });
    recommendedAction = t(
      `ui.recommendedActions.${d.overallRiskLevelKey}`,
      "—",
    );
    recommendedResponse = t(
      `ui.recommendedResponses.${d.overallRiskLevelKey}`,
      "—",
    );
    meta = {
      targetUrl: apiData.url ?? apiData.domain,
      detectedAt: apiData.created ?? "—",
      reportId: apiData.request_id ?? "—",
      engineVersion: "PhishDetector v4.0",
    };
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", background: "#f4f4f4" }}>
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 1,
          borderBottom: "1px solid #ddd",
          background: "#fff",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          PhishDetector
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            onClick={() => navigate("/url-lookup")}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: 15,
              color: "#333",
              border: "1px solid #ddd",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t("ui.backButton", "← Back to URL Lookup")}
          </Box>

          <Select
            value={currentLang === "ja" ? "ja" : "en"}
            onChange={(e) => {
              i18n.changeLanguage(e.target.value);
              localStorage.setItem("phishcompass_lang", e.target.value === "ja" ? "jp" : "en");
            }}
            size="small"
            sx={{ fontSize: 14, height: 28, minWidth: 70 }}
          >
            <MenuItem value="en">EN</MenuItem>
            <MenuItem value="ja">JP</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Loading overlay */}
      {loading && (
        <Dialog open aria-labelledby="report-search-wait">
          <DialogTitle id="report-search-wait">
            {t("ui.search.waitTitle")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ whiteSpace: "pre-line" }}>
              {t("ui.search.waitMessage")}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}

      {/* Registration completed dialog */}
      <Dialog open={regisDialog} onClose={() => setRegisDialog(false)}>
        <DialogTitle>{t("ui.search.registeredTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {t("ui.search.registeredMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisDialog(false)} autoFocus>
            {t("ui.search.ok")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert */}
      {showAlert && (
        <Stack
          sx={{ position: "fixed", top: 70, right: 0, zIndex: 9999 }}
          spacing={2}
        >
          <Alert variant="filled" severity={alertSeverity}>
            {alertMessage}
          </Alert>
        </Stack>
      )}

      {/* Search form */}
      <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4, px: 1 }}>
        <Box
          sx={{ background: "#fff", border: "1px solid #ddd", px: 4, py: 3 }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
            {t("ui.search.label")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <InputBase
              value={searchText}
              placeholder={t("ui.search.placeholder")}
              onChange={(e) => {
                setSearchText(e.target.value);
                setValidateSearch(false);
              }}
              onKeyPress={handleEnter}
              sx={{
                flexGrow: 1,
                height: 38,
                border: `1px solid ${validateSearch ? "red" : "#ccc"}`,
                borderRadius: "4px",
                px: 1.5,
                fontSize: 16,
              }}
              inputProps={{ maxLength: 200 }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{
                height: 38,
                backgroundColor: "#3a5eac",
                textTransform: "none",
                "&:hover": { backgroundColor: "#2d2f6b" },
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                t("ui.search.button")
              )}
            </Button>
          </Box>
          {validateSearch && (
            <Typography sx={{ color: "red", fontSize: 15, mt: 0.5 }}>
              {t("ui.search.required")}
            </Typography>
          )}
        </Box>
      </Box>

      {/* No data placeholder */}
      {!apiData && (
        <Box sx={{ maxWidth: 1200, mx: "auto", py: 6, textAlign: "center" }}>
          <Typography sx={{ color: "#999", fontSize: 17 }}>
            {t("ui.noReportData")}
          </Typography>
        </Box>
      )}

      {/* Report body */}
      {apiData && (
        <Box sx={{ maxWidth: 1200, mx: "auto", py: 5, px: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              background: "#fff",
              border: "1px solid #ddd",
              px: { xs: 2, md: 4 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {t("ui.reportTitle")}
            </Typography>

            <Box sx={{ fontSize: 15, lineHeight: 2, color: "#333", wordBreak: "break-word" }}>
              <Box>
                <strong>{t("ui.meta.targetUrl")}:</strong>{" "}
                <span
                  style={{
                    color: "#666",
                    wordBreak: "break-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {meta.targetUrl}
                </span>
              </Box>
              <Box>
                <strong>{t("ui.meta.detectedAt")}:</strong> {meta.detectedAt}
              </Box>
              <Box>
                <strong>{t("ui.meta.reportId")}:</strong> {meta.reportId}
              </Box>
              <Box>
                <strong>{t("ui.meta.engineVersion")}:</strong>{" "}
                {meta.engineVersion}
              </Box>
            </Box>

            {/* ── Layer 1 ── */}
            <LayerHeader num={1} title={t("ui.layers.1")} />

            {/* A. Overall Verdict */}
            <SectionHeader letter="A" title={t("ui.sections.A")} />
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="40%">{t("ui.tableHeaders.item")}</TH>
                  <TH>{t("ui.tableHeaders.value")}</TH>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TD>{t("ui.verdictLabel")}</TD>
                  <TD>
                    <VerdictCell verdict={d.overallVerdict.verdict} />
                  </TD>
                </tr>
                <tr>
                  <TD>{t("ui.riskLevelLabel")}</TD>
                  <TD>
                    <strong>
                      {t(
                        `ui.riskLevelLabels.${d.overallVerdict.riskLevel}`,
                        d.overallVerdict.riskLevel,
                      )}
                    </strong>
                  </TD>
                </tr>
                <tr>
                  <TD>{t("ui.recommendedActionLabel")}</TD>
                  <TD>
                    <strong>{recommendedAction}</strong>
                  </TD>
                </tr>
                <tr>
                  <TD>{t("ui.strengthOfEvidenceLabel")}</TD>
                  <TD>
                    <strong>{strengthOfEvidence}</strong>
                  </TD>
                </tr>
              </tbody>
            </table></Box>

            {/* B. Threat Types */}
            <SectionHeader letter="B" title={t("ui.sections.B")} />
            <SubNote>{t("ui.subNotes.B")}</SubNote>
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="5%">{t("ui.tableHeaders.no")}</TH>
                  <TH width="21%">{t("ui.tableHeaders.threatType")}</TH>
                  <TH width="13%">{t("ui.tableHeaders.confidence")}</TH>
                  <TH>{t("ui.tableHeaders.basis")}</TH>
                  <TH width="17%">{t("ui.tableHeaders.riskLevel")}</TH>
                </tr>
              </thead>
              <tbody>
                {d.threatTypesCritical.length === 0 ? (
                  <tr>
                    <TD
                      colSpan={5}
                      style={{ color: "#999", textAlign: "center" }}
                    >
                      {t("ui.noRecords")}
                    </TD>
                  </tr>
                ) : (
                  d.threatTypesCritical.map((row) => (
                    <tr key={row.no}>
                      <TD>{row.no}</TD>
                      <TD>
                        {t(`ui.threats.${row.code}`, {
                          defaultValue: row.type,
                        })}
                      </TD>
                      <TD>
                        {t(`ui.confidenceLabels.${row.confidence}`, {
                          defaultValue: row.confidence,
                        })}
                      </TD>
                      <TD>
                        {t(`ui.threatsBasis.${row.basisKey}`, {
                          ...row.basisParams,
                          defaultValue: row.basis,
                        })}
                      </TD>
                      <TD>
                        <RiskCell level={row.riskLevel} />
                      </TD>
                    </tr>
                  ))
                )}
              </tbody>
            </table></Box>
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                background: "#f0f4ff",
                border: "1px solid #c8d6f5",
                borderRadius: 1,
                fontSize: 15,
              }}
            >
              <strong>{t("ui.recommendedResponseLabel")}:</strong>{" "}
              {recommendedResponse}
            </Box>

            {/* ── Layer 2 ── */}
            <LayerHeader num={2} title={t("ui.layers.2")} />

            {/* C. AI Inspection — only detectors that flagged phishing */}
            <SectionHeader letter="C" title={t("ui.sections.C")} />
            <SubNote>{t("ui.subNotes.C")}</SubNote>
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="24%">{t("ui.tableHeaders.inspectionAspect")}</TH>
                  <TH width="14%">{t("ui.tableHeaders.status")}</TH>
                  <TH width="14%">{t("ui.tableHeaders.verdict")}</TH>
                  <TH width="14%">{t("ui.tableHeaders.riskLevel")}</TH>
                  <TH>{t("ui.tableHeaders.logicSummary")}</TH>
                </tr>
              </thead>
              <tbody>
                {d.aiInspectionSummary.length === 0 ? (
                  <tr>
                    <TD
                      colSpan={5}
                      style={{ color: "#999", textAlign: "center" }}
                    >
                      {t("ui.noRecords")}
                    </TD>
                  </tr>
                ) : (
                  d.aiInspectionSummary.map((row) => (
                    <tr key={row.aspect}>
                      <TD>
                        {t(`ui.detectorAspects.${row.detKey}`, row.aspect)}
                      </TD>
                      <TD>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              background: "#28a745",
                              borderRadius: "2px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "#fff",
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              ✓
                            </span>
                          </Box>
                          {t(
                            `ui.executionStatusLabels.${row.executionStatus}`,
                            row.executionStatus,
                          )}
                        </Box>
                      </TD>
                      <TD>
                        <VerdictCell verdict={row.verdict} />
                      </TD>
                      <TD>
                        {row.riskLevel ? (
                          <RiskCell level={row.riskLevel} color={row.riskColor} />
                        ) : (
                          "—"
                        )}
                      </TD>
                      <TD style={{ color: "#555" }}>
                        {t(`ui.logicSummaries.${row.detKey}`, "—")}
                      </TD>
                    </tr>
                  ))
                )}
                <tr style={{ background: "#fafafa" }}>
                  <TD style={{ fontWeight: 700 }}>
                    {t("ui.detectionResultLabel")}
                  </TD>
                  <TD></TD>
                  <TD style={{ fontWeight: 700 }}>
                    <VerdictCell verdict={d.detectionResult.verdict} />
                  </TD>
                  <TD style={{ fontWeight: 700 }}>
                    <RiskCell level={d.detectionResult.riskLevel} />
                  </TD>
                  <TD style={{ fontWeight: 700 }}>{strengthOfEvidence}</TD>
                </tr>
              </tbody>
            </table></Box>

            {/* D. Site Characteristics — top-5 phishing / non-phishing elements */}
            <SectionHeader letter="D" title={t("ui.sections.D")} />
            <SubNote>
              {d.siteCharIsPhish
                ? t("ui.subNotes.D_phish")
                : t("ui.subNotes.D_safe")}
            </SubNote>
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="5%">{t("ui.tableHeaders.no")}</TH>
                  <TH width="38%">{t("ui.tableHeaders.observedFact")}</TH>
                  <TH>{t("ui.tableHeaders.meaning")}</TH>
                  <TH width="16%">{t("ui.tableHeaders.evaluation")}</TH>
                </tr>
              </thead>
              <tbody>
                {d.siteCharTop.length === 0 ? (
                  <tr>
                    <TD
                      colSpan={4}
                      style={{ color: "#999", textAlign: "center" }}
                    >
                      {t("ui.noRecords")}
                    </TD>
                  </tr>
                ) : (
                  d.siteCharTop.map((row, i) => {
                    const ratio = d.siteCharIsPhish
                      ? row.positive_ratio
                      : row.negative_ratio;
                    const pct = ((ratio ?? 0) * 100).toFixed(1);
                    return (
                      <tr key={`${row.column}-${i}`}>
                        <TD>{i + 1}</TD>
                        <TD style={{ wordBreak: "break-all" }}>
                          {/* OLD: translated column label
                          {t(`ui.metaColumns.${row.column}`, row.column)} = {String(row.value)}
                          */}
                          {row.column} = {String(row.value)}
                        </TD>
                        <TD>
                          {d.siteCharIsPhish
                            ? t("ui.metaMeaning.phish", { pct })
                            : t("ui.metaMeaning.legit", { pct })}
                        </TD>
                        <TD style={{ whiteSpace: "nowrap" }}>
                          {d.siteCharIsPhish
                            ? `🔴 ${t("ui.evaluationLabels.phish")}`
                            : `🟢 ${t("ui.evaluationLabels.legit")}`}
                        </TD>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table></Box>

            {/* ── Layer 3 ── */}
            <LayerHeader num={3} title={t("ui.layers.3")} />

            {/* E. AI Inspection — all detectors */}
            <SectionHeader letter="E" title={t("ui.sections.E")} />
            <SubNote>{t("ui.subNotes.E")}</SubNote>
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="24%">{t("ui.tableHeaders.inspectionAspect")}</TH>
                  <TH width="14%">{t("ui.tableHeaders.executionStatus")}</TH>
                  <TH width="28%">{t("ui.tableHeaders.skipReason")}</TH>
                  <TH>{t("ui.tableHeaders.notes")}</TH>
                </tr>
              </thead>
              <tbody>
                {d.aiInspectionDetail.length === 0 ? (
                  <tr>
                    <TD
                      colSpan={4}
                      style={{ color: "#999", textAlign: "center" }}
                    >
                      {t("ui.noRecords")}
                    </TD>
                  </tr>
                ) : (
                  d.aiInspectionDetail.map((row) => (
                    <tr key={row.aspect}>
                      <TD>
                        {t(`ui.detectorAspects.${row.detKey}`, row.aspect)}
                      </TD>
                      <TD>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {row.executionStatus === "Completed" && (
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                background: "#28a745",
                                borderRadius: "2px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span
                                style={{
                                  color: "#fff",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                ✓
                              </span>
                            </Box>
                          )}
                          {row.executionStatus === "Error" && (
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                background: "#dc3545",
                                borderRadius: "2px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span
                                style={{
                                  color: "#fff",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                ✕
                              </span>
                            </Box>
                          )}
                          {row.executionStatus === "Not run" && (
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                background: "#ffc107",
                                borderRadius: "2px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span
                                style={{
                                  color: "#fff",
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                —
                              </span>
                            </Box>
                          )}
                          {t(
                            `ui.executionStatusLabels.${row.executionStatus}`,
                            row.executionStatus,
                          )}
                        </Box>
                      </TD>
                      <TD>
                        {row.executionStatus === "Completed" ? (
                          "—"
                        ) : (
                          <span style={{ color: "#999" }}>
                            {resolveApiMessage(row.skipReason, row.detKey, t)}
                          </span>
                        )}
                      </TD>
                      <TD>
                        {row.executionStatus === "Completed" ? (
                          <DetectorNotes
                            detKey={row.detKey}
                            rawDet={row.rawDet}
                          />
                        ) : (
                          "—"
                        )}
                      </TD>
                    </tr>
                  ))
                )}
              </tbody>
            </table></Box>

            {/* F. Site Characteristics — all findings */}
            <SectionHeader letter="F" title={t("ui.sections.F")} />
            <SubNote>{t("ui.subNotes.F")}</SubNote>
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="5%">{t("ui.tableHeaders.no")}</TH>
                  <TH width="35%">{t("ui.tableHeaders.observedFact")}</TH>
                  <TH>{t("ui.tableHeaders.meaning")}</TH>
                  <TH width="12%">{t("ui.tableHeaders.strength")}</TH>
                </tr>
              </thead>
              <tbody>
                {d.findingsAll.length === 0 ? (
                  <tr>
                    <TD
                      colSpan={4}
                      style={{ color: "#999", textAlign: "center" }}
                    >
                      {t("ui.noRecords")}
                    </TD>
                  </tr>
                ) : (
                  d.findingsAll.map((row, i) => (
                    <tr key={`${row.rule_id}-${i}`}>
                      <TD>{i + 1}</TD>
                      <TD>
                        {t(`ui.findings.${row.rule_id}`, {
                          defaultValue: row.description,
                        })}
                      </TD>
                      <TD>
                        {t(`ui.findingsDetail.${row.detail_key}`, {
                          ...row.detail_params,
                          defaultValue: row.detail,
                        })}
                      </TD>
                      <TD>
                        {row.strength ? (
                          <RiskCell
                            level={
                              row.strength.charAt(0).toUpperCase() +
                              row.strength.slice(1)
                            }
                          />
                        ) : (
                          "—"
                        )}
                      </TD>
                    </tr>
                  ))
                )}
              </tbody>
            </table></Box>

            {/* G. Threat Types */}
            <SectionHeader letter="G" title={t("ui.sections.G")} />
            <SubNote>{t("ui.subNotes.G")}</SubNote>
            <Box sx={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
              <thead>
                <tr>
                  <TH width="5%">{t("ui.tableHeaders.no")}</TH>
                  <TH width="22%">{t("ui.tableHeaders.threatType")}</TH>
                  <TH width="14%">{t("ui.tableHeaders.confidence")}</TH>
                  <TH>{t("ui.tableHeaders.basis")}</TH>
                  <TH width="16%">{t("ui.tableHeaders.riskLevel")}</TH>
                </tr>
              </thead>
              <tbody>
                {d.threatTypesAll.length === 0 ? (
                  <tr>
                    <TD
                      colSpan={5}
                      style={{ color: "#999", textAlign: "center" }}
                    >
                      {t("ui.noRecords")}
                    </TD>
                  </tr>
                ) : (
                  d.threatTypesAll.map((row) => (
                    <tr key={row.no}>
                      <TD>{row.no}</TD>
                      <TD>
                        {t(`ui.threats.${row.code}`, {
                          defaultValue: row.type,
                        })}
                      </TD>
                      <TD>
                        {t(`ui.confidenceLabels.${row.confidence}`, {
                          defaultValue: row.confidence,
                        })}
                      </TD>
                      <TD>
                        {t(`ui.threatsBasis.${row.basisKey}`, {
                          ...row.basisParams,
                          defaultValue: row.basis,
                        })}
                      </TD>
                      <TD>
                        <RiskCell level={row.riskLevel} />
                      </TD>
                    </tr>
                  ))
                )}
              </tbody>
            </table></Box>

            <Box sx={{ pb: 6 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
